<script lang="ts">
	import { t } from '$i18n/i18n.ts'
	import type { Lot } from '../lot'
	import { createEventDispatcher, getContext, onMount } from 'svelte'

	import Salutation from '../../atoms/Salutation.svelte'
	import InfoCard from '../../organisms/InfoCard.svelte'
	import ProjectInformationGrid from '../../project/ProjectInformationGrid.svelte'
	import TextArea from '../../atoms/TextArea.svelte'
	import FileUpload from '../../organisms/FileUpload.svelte'
	import { CalcMethod, OfferViewMode } from '$types/enums'
	import OptimizerDecisionButtons from '../../bid/OptimizerDecisionButtons.svelte'
	import OfferInfo from '../../bid/OfferInfo.svelte'
	import PrimaryButton, { PrimaryBtnColors } from '../../atoms/PrimaryButton.svelte'
	import RejectOfferComponent from '../RejectOfferComponent.svelte'
	import { userInfo, userIsLoggedIn } from '../../stores'
	import AcceptOfferComponent from '../../bid/AcceptOfferComponent.svelte'
	import PublicPageHeader from '../../molecules/PublicPageHeader.svelte'
	import PublicPageFooter from '../../molecules/PublicPageFooter.svelte'
	import Basic from '../../templates/Basic.svelte'
	import { fetchRequestTenantLogo } from '../../corporateDesign/corporateDesignUtils'
	import OfferFactsForm from '../../bid/OfferFactsForm.svelte'
	import { type Offers, OfferState } from '../../bid/types/offer'
	import type { Writable } from 'svelte/store'
	import type { ContactData } from '$types/contactData'
	import type { UserInfo } from '$types/userInfo'
	import type { PubPageParams } from '../../bid/types/pubPageParams'
	import { checkEmptyOffers, loadOfferAttachments } from '../../bid/offerUtils'
	import { type RequestDto } from '../../bid/types/requestDto'
	import ProjectNavigationButtons from '../../molecules/ProjectNavigationButtons.svelte'
	import { NextButtonWantedObject } from '../../bid/types/nextButtonDto'
	import ContactBox from '../../atoms/ContactBox.svelte'

	const lot: Writable<Lot> = getContext('lot')
	const request: Writable<RequestDto | null> = getContext('request')
	const offers: Writable<Offers> = getContext('offers')
	const urlParams: PubPageParams | null = getContext('urlParams') || null
	const reloadRequest: () => Promise<void> = getContext('reloadRequest')

	export let optimizerContactData: ContactData

	const dispatch = createEventDispatcher()

	let offerViewMode: OfferViewMode = OfferViewMode.VIEW_OFFER

	let attachmentsOffer: any[] = []
	let attachmentsUploadUrl: string
	let attachmentsDeletedUrl: string
	let attachmentLinkUrl: string
	let linkAttachments
	let currentUserInfo: UserInfo
	let isDifferentTenant: boolean = false
	let logo: string = ''
	let emptyOffers: boolean

	onMount(() => {
		attachmentsUploadUrl = `/api/request/${urlParams?.requestPublicId}/requested/${urlParams?.requestedEntityType}/${urlParams?.requestedEntityId}/user/${urlParams?.offerUserPublicId}/attachment/new`
		if (
			$offers?.material?.publicId ||
			$offers?.materialWithTransport?.publicId ||
			$offers?.transport?.publicId
		) {
			let id =
				$offers?.material?.publicId ||
				$offers?.materialWithTransport?.publicId ||
				$offers?.transport?.publicId
			attachmentsUploadUrl = `/api/offer/${id}/attachment/new`
			attachmentsDeletedUrl = `/api/offer/${id}/attachment/delete`
			attachmentLinkUrl = `/api/offer/${id}/attachment/link`
			// at the moment, we only need to load attachments of one offer, since we sync them for both offers, will be adapted later
			loadOfferAttachments($offers, urlParams).then((data) => {
				attachmentsOffer = data
			})
		}
	})

	function linkAttachmentsByAccept() {
		linkAttachments()
	}

	async function fetchLogo() {
		logo = (await fetchRequestTenantLogo(urlParams?.requestPublicId)) ?? ''
	}

	function reloadAttachments(data: any) {
		if ((data?.length || -1) > 0) {
			attachmentsOffer = data
		}
	}

	function handleUploadOfferAttachmentWithoutOffer(responseData?: Offers) {
		// Set publicId of draft offer if an attachment was uploaded without an offer, s.t. the uploaded attachment is
		// fetched correctly without reloading the page.
		if (Object.values($offers).every((offer) => !offer?.publicId) && responseData) {
			Object.keys($offers).forEach((key) => {
				if (responseData[key]) {
					$offers[key].publicId = responseData[key]?.publicId
					$offers[key].offerState = OfferState.DRAFT
				}
			})
		}
	}

	userInfo.subscribe((value) => (currentUserInfo = value))

	const offerChanged = () => dispatch('offerChanged')
	const viewOffer = () => (offerViewMode = OfferViewMode.VIEW_OFFER)
	const viewEditOffer = () => (offerViewMode = OfferViewMode.EDIT_OFFER)
	const viewAcceptOffer = () => (offerViewMode = OfferViewMode.ACCEPT_OFFER)
	const viewRejectOffer = () => (offerViewMode = OfferViewMode.REJECT_OFFER)
	// reload before leaving edit mode, so the view mounts with fresh data
	const cancelEdit = async () => {
		try {
			await reloadRequest?.()
			viewOffer()
		} catch {
			/**
			 * reload failed (already surfaced via notification) - stay in edit mode rather than
			 * switching to a view that may not reflect the discarded local edits
			 */
		}
	}

	$: if (currentUserInfo?.tenantName && $lot?.tenantName) {
		isDifferentTenant = currentUserInfo.tenantName !== $lot.tenantName
	}

	$: if ($lot?.isSaasInternalLot && urlParams?.requestPublicId) {
		fetchLogo()
	}

	// the variable is used/assigned reactively, so the unused expression flag hier is wrong
	// eslint-disable-next-line @typescript-eslint/no-unused-expressions
	$: ($offers.material,
		$offers.materialWithTransport,
		$offers.transport,
		(emptyOffers = checkEmptyOffers($offers)))

	function getFilePrefix(): string {
		let filePrefix = $t('UI.attachments.optimizer.prefix')
		if (currentUserInfo.tenantName) {
			filePrefix += currentUserInfo.tenantName + '_' + currentUserInfo.userLogin + '-'
		} else {
			filePrefix += optimizerContactData.fullName?.replace(/\s/g, '_') + '-'
		}
		return filePrefix
	}
</script>

{#if !$userIsLoggedIn || isDifferentTenant}
	<PublicPageHeader requestPublicId={urlParams?.requestPublicId} />
{/if}

<Basic showStandardHeaderFooter={$userIsLoggedIn && !isDifferentTenant}>
	{#if $lot.isSaasInternalLot && !isDifferentTenant && $userIsLoggedIn && logo}
		<div class="pp-logo-top">
			<img alt="Logo" src={logo} />
		</div>
	{/if}
	<div class="container-salutation-contact">
		<Salutation
			name={optimizerContactData.fullName}
			saasOwnerName={$lot.ownerName}
			tenantName={$lot.tenantName}
			showSaasData
		/>
		{#if $userIsLoggedIn && !isDifferentTenant}
			<ContactBox />
		{/if}
	</div>

	<div class="row">
		<div class="col-lg-5 col-md-12 col-sm-12">
			<InfoCard />
		</div>
		<div class="col-lg-7 col-md-12 col-sm-12">
			<div class="container-project-information">
				<ProjectInformationGrid title={$t('UI.project.information')} {...$lot} />
				<TextArea
					title={$t('UI.materialDetailsComment.header')}
					text={$lot.lotRemarks}
					placeholder={$t('UI.remarks.noRemarks')}
					readonly
				/>
				<TextArea
					title={$t('UI.projectDetailsComment.header')}
					text={$lot.projectRemarks || ''}
					placeholder={$t('UI.remarks.noRemarks')}
					readonly
					isHtml
				/>
			</div>
		</div>
	</div>
	<div class="row" style="margin-top: 1.875rem">
		<div class="col-lg-12 col-sm-12">
			<FileUpload
				uploadUrl={attachmentsUploadUrl}
				deleteUrl={attachmentsDeletedUrl}
				parentPublicId={$offers.material.publicId}
				linkUrl={attachmentLinkUrl}
				filePrefix={getFilePrefix()}
				visibleToCustomer={0}
				visibleToPartner={1}
				bind:eventByAccept={linkAttachments}
				updateFun={() =>
					loadOfferAttachments($offers, urlParams).then((data) => reloadAttachments(data))}
				attachments={attachmentsOffer}
				canNotDelete={Object.values($offers).some((o) => o.offerState !== OfferState.DRAFT)}
				onUploadComplete={handleUploadOfferAttachmentWithoutOffer}
			/>
		</div>
	</div>

	{#if offerViewMode === OfferViewMode.VIEW_OFFER}
		{#if !emptyOffers}
			<div class="col-lg-12 col-sm-12 columns" style="margin-top: 1.875rem">
				<OfferInfo onOptimizerPage />
				<div class="align-right">
					<PrimaryButton
						label={$lot.calcMethod === CalcMethod.REQUEST_OFFER
							? $t('UI.optimizerPage.priceOffer.editor.title.edit')
							: $t('UI.optimizerPage.counterOffer.editor.title.edit')}
						color={PrimaryBtnColors.ORANGE}
						on:click={viewEditOffer}
					/>
				</div>
			</div>
		{:else}
			<OptimizerDecisionButtons
				calcMethod={$lot.calcMethod!}
				onAcceptOffer={viewAcceptOffer}
				onDeclineOffer={viewRejectOffer}
				onEditOffer={viewEditOffer}
			/>
		{/if}
	{/if}
	{#if offerViewMode === OfferViewMode.ACCEPT_OFFER}
		<AcceptOfferComponent
			hideCheckbox
			isExternalUser
			on:cancel={viewOffer}
			on:offerChanged={offerChanged}
			on:linkAttachmentsByAccept={linkAttachmentsByAccept}
		/>
	{/if}

	{#if offerViewMode === OfferViewMode.EDIT_OFFER}
		<OfferFactsForm
			hideAgbCheckbox
			on:cancelEdit={cancelEdit}
			on:linkAttachmentsByAccept={linkAttachmentsByAccept}
			on:offerChanged={() => {
				offerChanged()
				viewOffer()
			}}
		/>
	{/if}

	{#if offerViewMode === OfferViewMode.REJECT_OFFER}
		<RejectOfferComponent pub on:cancel={viewOffer} on:offerChanged={offerChanged} />
	{/if}

	{#if $userIsLoggedIn}
		<div class="row">
			<div class="col-lg-12 col-sm-12">
				<ProjectNavigationButtons
					currentId={$request.publicId}
					wantedObject={NextButtonWantedObject.REQUEST}
					returnUrl="/tenantlots/bid"
					isSaas
				/>
			</div>
		</div>
	{/if}
</Basic>

{#if !$userIsLoggedIn || isDifferentTenant}
	<PublicPageFooter />
{/if}

<style lang="scss">
	@import '$styles/pubPage';

	.columns {
		@include flex-col(1.875rem, $alignment: flex-end);

		width: 100%;
	}
</style>
